/**
 * MarketContext
 *
 * Fetches broker configs + market snapshot at the Layout level (once, not per-page).
 * All pages/components read from this context, so navigating between pages never
 * resets the state and the header never flickers back to "Connect broker".
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const MarketContext = createContext(null);

export function MarketProvider({ children }) {
  const [snap, setSnap] = useState(null);
  const [brokerConfig, setBrokerConfig] = useState(null); // live-feed / any-connected broker
  const mountedRef = useRef(true);

  const loadBrokers = useCallback(() => {
    api.get("/broker/configs")
      .then((r) => {
        if (!mountedRef.current) return;
        const liveFeed = r.data.find((c) => c.use_for_market && c.is_connected);
        const anyConnected = r.data.find((c) => c.is_connected);
        setBrokerConfig(liveFeed || anyConnected || null);
      })
      .catch(() => {});
  }, []);

  const loadSnap = useCallback(() => {
    api.get("/market/snapshot")
      .then((r) => { if (mountedRef.current) setSnap(r.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Load broker config immediately — only needs to refresh when user changes settings
    loadBrokers();

    // Snapshot polls every 3 s
    loadSnap();
    const t = setInterval(loadSnap, 1000);

    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, [loadBrokers, loadSnap]);

  return (
    <MarketContext.Provider value={{ snap, brokerConfig, refreshBrokers: loadBrokers }}>
      {children}
    </MarketContext.Provider>
  );
}

export function useMarket() {
  return useContext(MarketContext);
}
